import { useCallback, useEffect, useMemo, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ReactFlow, {
  Background,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  NodeProps,
  Position,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';

import {
  AccountabilityChartScope,
  useGetAccountabilityChart,
} from 'services/v1/Tenant/AccountabilityService';
import { getErrorMessage } from 'utils/Error';
import {
  ACCOUNTABILITY_CHART_NODE_SIZE,
  AccountabilityFlowNodeData,
  layoutAccountabilityChart,
} from './accountabilityChartLayout';
import { getAccountabilityAvatarColor } from './accountabilityAvatarColor';
import {
  AccountabilityLevelFilter,
  deriveAccountabilityLevelFilters,
  filterAccountabilityTreeToSelection,
  restrictAccountabilityLevelOptions,
} from './accountabilityLevelFilters';
import { deriveAccountabilityUserFilterOptions, filterAccountabilityTreeByUser } from './accountabilityUserFilters';

export interface AccountabilityChartDialogProps {
  open: boolean;
  definitionId?: string;
  definitionName?: string;
  chartScope?: AccountabilityChartScope;
  onClose: () => void;
}

const ACCENT_BLUE = '#3B4797';
const ACCENT_GREEN = '#42BB93';
const TEXT_SECONDARY = '#667085';

interface AccountabilityGroupNodeData {
  height: number;
  label: string;
  width: number;
}

function AccountabilityGroupNode({ data }: NodeProps<AccountabilityGroupNodeData>) {
  return (
    <Box
      sx={{
        width: data.width,
        height: data.height,
        pointerEvents: 'none',
        bgcolor: 'rgba(248, 249, 252, 0.72)',
        border: '2px solid #756BC7',
        borderRadius: '7px',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Stack
        height={32}
        alignItems='center'
        justifyContent='center'
        sx={{ borderBottom: '1px solid #B9B4E2', bgcolor: 'rgba(255, 255, 255, 0.86)', px: 2 }}
      >
        <Typography noWrap title={data.label} sx={{ color: '#344054', fontSize: 12.5, fontWeight: 800 }}>
          {data.label}
        </Typography>
      </Stack>
    </Box>
  );
}

function AccountabilityNodeCard({ data, id }: NodeProps<AccountabilityFlowNodeData>) {
  const { accountability, directReportCount, hasChildren, isCollapsed, isRoot, onToggle, showDirectReportCount = true } = data;
  const isMapped = accountability.mapped;
  const memberLabel = accountability.member?.trim() || 'Unnamed member';
  const personLabel = accountability.accountablePerson?.label;
  const displayedPerson = personLabel || (isRoot ? 'Hierarchy root' : isMapped ? 'Unassigned' : 'Not mapped');
  const avatarLabel = personLabel?.trim().charAt(0).toUpperCase() || (isRoot ? 'H' : '?');
  const avatarColor = getAccountabilityAvatarColor(accountability.accountablePerson?.id);
  const levelLabel = accountability.levelName || (isRoot ? 'Top' : `Level ${accountability.level ?? '-'}`);

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onToggle?.(id);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: ACCOUNTABILITY_CHART_NODE_SIZE.width,
        height: ACCOUNTABILITY_CHART_NODE_SIZE.height,
        boxSizing: 'border-box',
        bgcolor: '#fff',
        border: '1px solid #E4EAF1',
        borderRadius: 1.5,
        boxShadow: '0 3px 12px rgba(16, 24, 40, 0.08)',
        px: 1.25,
        py: 1,
      }}
    >
      {!isRoot && (
        <Handle
          type='target'
          position={Position.Left}
          isConnectable={false}
          style={{ width: 6, height: 6, border: 0, background: '#98A2AE', opacity: 0 }}
        />
      )}

      <Typography
        noWrap
        title={levelLabel}
        sx={{
          pr: hasChildren ? 4 : 0,
          color: '#5F748B',
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '0.7px',
          lineHeight: 1.2,
          textTransform: 'uppercase',
        }}
      >
        {levelLabel}
      </Typography>

      <Typography
        noWrap
        title={memberLabel}
        sx={{ mt: 0.75, pr: hasChildren ? 4 : 0, color: '#101828', fontSize: 15, fontWeight: 800, lineHeight: 1.3 }}
      >
        {memberLabel}
      </Typography>

      {hasChildren && (
        <IconButton
          className='nodrag nopan'
          aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${memberLabel}`}
          size='small'
          onClick={handleToggle}
          sx={{
            position: 'absolute',
            top: 7,
            right: 9,
            width: 25,
            height: 25,
            color: '#5F748B',
            border: '1px solid #D7E1EA',
            borderRadius: '7px',
            bgcolor: '#fff',
            '&:hover': { bgcolor: '#F8FAFC', borderColor: '#BFCBD8' },
          }}
        >
          {isCollapsed ? <AddRoundedIcon sx={{ fontSize: 15 }} /> : <RemoveRoundedIcon sx={{ fontSize: 15 }} />}
        </IconButton>
      )}

      <Stack direction='row' alignItems='center' gap={0.75} sx={{ mt: 1.25, minWidth: 0 }}>
        <Stack direction='row' alignItems='center' gap={0.75} sx={{ minWidth: 0, flex: 1 }}>
          <Avatar
            sx={{
              width: 24,
              height: 24,
              flexShrink: 0,
              bgcolor: avatarColor,
              color: personLabel ? '#fff' : '#667085',
              fontSize: 11,
              fontWeight: 800,
            }}
          >
            {avatarLabel}
          </Avatar>
          <Typography
            noWrap
            title={displayedPerson}
            sx={{ color: personLabel ? '#344054' : '#667085', fontSize: 12, fontWeight: 700, lineHeight: 1.2 }}
          >
            {displayedPerson}
          </Typography>
        </Stack>

        {hasChildren && showDirectReportCount && (
          <Box
            sx={{
              flexShrink: 0,
              borderRadius: '999px',
              bgcolor: '#F2F4F7',
              color: TEXT_SECONDARY,
              px: 1.1,
              py: 0.65,
              fontSize: 11,
              fontWeight: 500,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
            }}
          >
            {directReportCount} direct report{directReportCount === 1 ? '' : 's'}
          </Box>
        )}
      </Stack>

      {hasChildren && (
        <Handle
          type='source'
          position={Position.Right}
          isConnectable={false}
          style={{ width: 6, height: 6, border: 0, background: ACCENT_GREEN, opacity: 0 }}
        />
      )}
    </Box>
  );
}

const accountabilityNodeTypes = {
  accountability: AccountabilityNodeCard,
  accountabilityGroup: AccountabilityGroupNode,
};

export function AccountabilityChartDialog({
  open,
  definitionId,
  definitionName,
  chartScope = 'all',
  onClose,
}: AccountabilityChartDialogProps) {
  const { data, isError, isFetching, error } = useGetAccountabilityChart(
    open ? definitionId : undefined,
    chartScope
  );
  const [collapsedNodeIds, setCollapsedNodeIds] = useState<Set<string>>(() => new Set());
  const [selectedLevelNodeIds, setSelectedLevelNodeIds] = useState<Record<string, string>>({});
  const [selectedUserId, setSelectedUserId] = useState<string>();
  const chart = data?.data;
  const levelFilters = useMemo(() => deriveAccountabilityLevelFilters(chart?.root), [chart?.root]);
  const selectedNodeIds = useMemo(
    () => levelFilters.map((level) => selectedLevelNodeIds[level.key]).filter(Boolean),
    [levelFilters, selectedLevelNodeIds]
  );
  const levelFilteredRoot = useMemo(
    () => (chart?.root ? filterAccountabilityTreeToSelection(chart.root, selectedNodeIds) : undefined),
    [chart?.root, selectedNodeIds]
  );
  const userFilterOptions = useMemo(
    () => deriveAccountabilityUserFilterOptions(levelFilteredRoot),
    [levelFilteredRoot]
  );
  const filteredRoot = useMemo(
    () => (levelFilteredRoot ? filterAccountabilityTreeByUser(levelFilteredRoot, selectedUserId) : undefined),
    [levelFilteredRoot, selectedUserId]
  );
  const activeFilterKey = useMemo(
    () => `${levelFilters.map((level) => selectedLevelNodeIds[level.key] || '').join('|')}|${selectedUserId || ''}`,
    [levelFilters, selectedLevelNodeIds, selectedUserId]
  );
  const hasActiveFilters = selectedNodeIds.length > 0 || Boolean(selectedUserId);

  useEffect(() => {
    if (open) {
      setCollapsedNodeIds(new Set());
      setSelectedLevelNodeIds({});
      setSelectedUserId(undefined);
    }
  }, [definitionId, open]);

  const handleLevelFilterChange = useCallback(
    (levelFilter: AccountabilityLevelFilter, selectedNodeId?: string) => {
      setSelectedLevelNodeIds((current) => {
        const next = { ...current };
        levelFilters.filter((level) => level.depth >= levelFilter.depth).forEach((level) => delete next[level.key]);
        if (selectedNodeId) next[levelFilter.key] = selectedNodeId;
        return next;
      });
      setSelectedUserId(undefined);
      setCollapsedNodeIds(new Set());
    },
    [levelFilters]
  );

  const handleUserFilterChange = useCallback((userId?: string) => {
    setSelectedUserId(userId);
    setCollapsedNodeIds(new Set());
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedLevelNodeIds({});
    setSelectedUserId(undefined);
    setCollapsedNodeIds(new Set());
  }, []);

  const handleToggleNode = useCallback((nodeId: string) => {
    setCollapsedNodeIds((current) => {
      const next = new Set(current);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  const elements = useMemo(() => {
    const layout = layoutAccountabilityChart(filteredRoot, collapsedNodeIds);
    const groupNodes = layout.groups.map((group) => ({
      id: group.id,
      type: 'accountabilityGroup',
      position: group.position,
      data: { label: group.label, width: group.width, height: group.height },
      draggable: false,
      selectable: false,
      connectable: false,
      focusable: false,
      zIndex: 0,
      style: { width: group.width, height: group.height, pointerEvents: 'none' as const },
    }));

    return {
      ...layout,
      nodes: [
        ...groupNodes,
        ...layout.nodes.map((node) => ({
          ...node,
          zIndex: 2,
          data: { ...node.data, onToggle: handleToggleNode, showDirectReportCount: !selectedUserId },
        })),
      ],
    };
  }, [collapsedNodeIds, filteredRoot, handleToggleNode, selectedUserId]);
  const chartLayoutKey = useMemo(
    () => `${definitionId || 'accountability'}-${activeFilterKey}-${[...collapsedNodeIds].sort().join('|')}`,
    [activeFilterKey, collapsedNodeIds, definitionId]
  );
  const isInitialLoading = isFetching && !chart;
  const title = chart?.definitionName || definitionName || 'Accountability Chart';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      aria-labelledby='accountability-chart-dialog-title'
      PaperProps={{
        sx: {
          width: '94vw',
          maxWidth: 1440,
          height: '86vh',
          maxHeight: 920,
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle id='accountability-chart-dialog-title' sx={{ px: 2.5, py: 1.5 }}>
        <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2}>
          <Stack minWidth={0}>
            <Typography noWrap sx={{ color: ACCENT_BLUE, fontSize: 16, fontWeight: 700 }}>
              {title}
            </Typography>
            <Typography noWrap sx={{ color: '#98A2AE', fontSize: 11 }}>
              {chart?.kTreeName ? `${chart.kTreeName} accountability chart` : 'Accountability chart'}
            </Typography>
          </Stack>
          <IconButton size='small' aria-label='Close accountability chart' onClick={onClose}>
            <HighlightOffRoundedIcon sx={{ color: '#98A2AE', fontSize: 22 }} />
          </IconButton>
        </Stack>

        {(levelFilters.length > 0 || userFilterOptions.length > 0) && (
          <Stack direction='row' alignItems='center' gap={1.25} flexWrap='wrap' sx={{ mt: 1.5 }}>
            <Typography sx={{ color: '#667085', fontSize: 11, fontWeight: 700, mr: 0.25 }}>Filter by</Typography>
            {levelFilters.map((levelFilter) => {
              const selectedHigherLevelIds = levelFilters
                .filter((level) => level.depth < levelFilter.depth)
                .map((level) => selectedLevelNodeIds[level.key]);
              const availableOptions = restrictAccountabilityLevelOptions(levelFilter, selectedHigherLevelIds);
              const selectedOption = levelFilter.options.find((option) => option.id === selectedLevelNodeIds[levelFilter.key]) ?? null;

              return (
                <Autocomplete
                  key={levelFilter.key}
                  size='small'
                  options={availableOptions}
                  value={selectedOption}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_, option) => handleLevelFilterChange(levelFilter, option?.id)}
                  noOptionsText='No members available'
                  sx={{
                    width: 185,
                    flexShrink: 0,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#fff',
                      fontSize: 12,
                    },
                    '& .MuiInputLabel-root': { fontSize: 12 },
                  }}
                  renderInput={(params) => <TextField {...params} label={levelFilter.label} placeholder='All' />}
                />
              );
            })}
            {userFilterOptions.length > 0 && (
              <Autocomplete
                size='small'
                options={userFilterOptions}
                value={userFilterOptions.find((option) => option.id === selectedUserId) ?? null}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, option) => handleUserFilterChange(option?.id)}
                noOptionsText='No users available'
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.label}
                  </li>
                )}
                sx={{
                  width: 200,
                  flexShrink: 0,
                  '& .MuiOutlinedInput-root': { bgcolor: '#fff', fontSize: 12 },
                  '& .MuiInputLabel-root': { fontSize: 12 },
                }}
                renderInput={(params) => <TextField {...params} label='User' placeholder='All' />}
              />
            )}
            {hasActiveFilters && (
              <Button
                size='small'
                onClick={handleClearFilters}
                sx={{
                  color: ACCENT_BLUE,
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                Clear filters
              </Button>
            )}
          </Stack>
        )}
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0, display: 'flex', minHeight: 0, position: 'relative', bgcolor: '#F8FAFC' }}>
        {isInitialLoading && (
          <Stack width='100%' alignItems='center' justifyContent='center' gap={1.5}>
            <CircularProgress size={32} sx={{ color: ACCENT_GREEN }} />
            <Typography variant='body2' color='text.secondary'>
              Loading accountability chart...
            </Typography>
          </Stack>
        )}

        {!isInitialLoading && isError && (
          <Stack width='100%' alignItems='center' justifyContent='center' px={3}>
            <Alert severity='error' sx={{ width: '100%', maxWidth: 640 }}>
              {error ? getErrorMessage(error) : 'Unable to load the accountability chart.'}
            </Alert>
          </Stack>
        )}

        {!isInitialLoading && !isError && elements.nodes.length === 0 && (
          <Stack width='100%' alignItems='center' justifyContent='center'>
            <Typography variant='body2' color='text.secondary'>
              {hasActiveFilters ? 'No accountability records match the selected filters.' : 'No accountability chart data is available.'}
            </Typography>
          </Stack>
        )}

        {!isInitialLoading && !isError && elements.nodes.length > 0 && (
          <Box sx={{ width: '100%', height: '100%', minHeight: 0 }}>
            <ReactFlowProvider>
              <ReactFlow
                key={chartLayoutKey}
                nodes={elements.nodes}
                edges={elements.edges}
                nodeTypes={accountabilityNodeTypes}
                fitView
                fitViewOptions={{ padding: 0.2, includeHiddenNodes: false }}
                minZoom={0.1}
                maxZoom={1.5}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable={false}
                defaultEdgeOptions={{
                  type: 'smoothstep',
                  zIndex: 1,
                  style: { stroke: '#98A2AE', strokeWidth: 1.4 },
                  markerEnd: { type: MarkerType.ArrowClosed, color: '#98A2AE', width: 16, height: 16 },
                }}
              >
                <Background gap={18} size={1} color='#D0D5DD' />
                <MiniMap
                  pannable
                  zoomable
                  nodeColor={(node) =>
                    node.type === 'accountabilityGroup' ? 'rgba(117, 107, 199, 0.16)' : ACCENT_BLUE
                  }
                  maskColor='rgba(248, 250, 252, 0.72)'
                />
                <Controls showInteractive={false} />
              </ReactFlow>
            </ReactFlowProvider>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AccountabilityChartDialog;
